/**
 * Role names shown on Access Management, and the outside-email warning.
 * Must match services/access/role-policy.js on the server.
 */

export const INTERNAL_EMAIL_DOMAIN = 'rapidfusion.co.uk';
const OUTSIDE_EMAIL_CODE = 'INTERNAL_ROLE_OUTSIDE_EMAIL';

export const ROLE_OPTIONS = [
  { value: 'external', label: 'External (customer)' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'client', label: 'Design Check guest', guest: true },
  { value: 'user', label: 'Internal staff (Rapid Fusion)', internal: true },
  { value: 'sales', label: 'Sales (Rapid Fusion)', internal: true },
  { value: 'admin', label: 'Admin (Rapid Fusion)', internal: true, adminOnly: true },
];

export function roleChoices({ canCreateAdmin = false, includeGuest = false } = {}) {
  return ROLE_OPTIONS.filter((option) => {
    if (option.guest && !includeGuest) return false;
    if (option.adminOnly && !canCreateAdmin) return false;
    return true;
  });
}

export function openingRole(user, canCreateAdmin) {
  const stored = user && user.role;
  if (stored === 'client') return 'external';
  if (roleChoices({ canCreateAdmin }).some((option) => option.value === stored)) return stored;
  return 'external';
}

export function roleLabel(role) {
  const option = ROLE_OPTIONS.find((o) => o.value === role);
  return option ? option.label : ROLE_OPTIONS[0].label;
}

export function isInternalRole(role) {
  return ROLE_OPTIONS.some((o) => o.value === role && o.internal);
}

export function isInternalEmail(email) {
  const value = String(email || '').trim().toLowerCase();
  const at = value.lastIndexOf('@');
  return at > 0 && value.slice(at + 1) === INTERNAL_EMAIL_DOMAIN;
}

/**
 * Sends a request that may assign a role. If the server refuses because an
 * internal role is going to an outside address, asks the admin to confirm
 * and resends with the override. Resolves with the final response data.
 */
export async function sendWithOutsideEmailConfirm(send, payload) {
  const data = await send(payload);
  if (data.code !== OUTSIDE_EMAIL_CODE) return data;
  const confirmed = window.confirm(
    `${data.error}\n\nThis gives an outside person access to internal Rapid Fusion tools and data. Grant it anyway?`
  );
  if (!confirmed) return { success: false, error: 'Cancelled. The role was not changed.' };
  return send({ ...payload, allowExternalInternalRole: true });
}
